'use client'

import { useClerk } from '@clerk/nextjs'
import { nanoid } from 'nanoid'
import { useTranslations } from 'next-intl'
import { useCallback, useState } from 'react'

import type { PromptInputMessage } from '@/components/ai-elements/prompt-input'
import { ChatPromptInputBar } from '@/components/chat/chat-prompt-input-bar'
import {
  buildPublicComposerDraft,
  writePublicComposerDraft,
} from '@/components/chat/public-composer-draft'
import { useAppLocale } from '@/components/i18n/locale-provider'
import { postAuthLandingPath } from '@/lib/auth/post-auth-landing'

interface PublicRemixPromptBoxProps {
  /** The template's detail URL — where sign-in returns the visitor. */
  detailHref: string
}

/**
 * Public template-remix composer. Renders the same `<ChatPromptInputBar>`
 * visual as the signed-in dock. On submit it persists the draft text through
 * the existing
 * public-composer-draft localStorage flow and opens Clerk's sign-in modal,
 * which lands on the post-auth route with the page's language and then
 * back on the template's detail URL, which renders in the signed-in app
 * shell once the visitor has an org (see `proxy.ts`).
 */
export function PublicRemixPromptBox({
  detailHref,
}: PublicRemixPromptBoxProps) {
  const t = useTranslations('templates')
  const [input, setInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { openSignIn } = useClerk()
  const landing = postAuthLandingPath(useAppLocale(), detailHref)

  const handleFetchChatMentions = useCallback(
    async () => Promise.resolve([]),
    []
  )

  const handleSubmit = useCallback(
    async (
      message: PromptInputMessage,
      event: React.FormEvent<HTMLFormElement>
    ) => {
      event.preventDefault()
      if (isSubmitting) {
        return
      }

      const text = message.text?.trim() ?? ''
      const files = message.files ?? []
      if (!text && files.length === 0) {
        return
      }

      setIsSubmitting(true)

      try {
        // Persist the draft so the user's prompt survives the auth round-trip.
        // Files captured pre-auth aren't replayed automatically yet (would
        // need IndexedDB filesKey wiring like LaunchComposer); plain text
        // is enough for the templates remix flow.
        writePublicComposerDraft(
          buildPublicComposerDraft({
            chatId: nanoid(),
            text,
            customBrandId: null,
            files: [],
          })
        )

        // Force the user back to this template after auth (through the
        // post-auth route, which keeps the page's language) so the signed-in
        // page's <TemplateChatAttach> can attach the template to the side
        // chat and restore the persisted draft into its composer for review.
        // `forceRedirectUrl` takes precedence over env vars and search
        // params (current Clerk API; replaces deprecated `redirectUrl`).
        openSignIn({
          forceRedirectUrl: landing,
        })
      } finally {
        setIsSubmitting(false)
      }
    },
    [landing, isSubmitting, openSignIn]
  )

  return (
    <ChatPromptInputBar
      inputLabel={t('detail.remixInputLabel')}
      boxOnly
      enableAutocomplete={false}
      handleFetchChatMentions={handleFetchChatMentions}
      // initialOrgId is unused by the bar (prefixed `_` in the source) but
      // is required by the prop type. Pass an empty string for public mode.
      initialOrgId=""
      input={input}
      isLoading={isSubmitting}
      onChangeInput={setInput}
      onSubmit={handleSubmit}
      resume={false}
      status="ready"
    />
  )
}
